import { ArrowUpRightFromSquare, HandPointUp } from "@gravity-ui/icons";
import { Card, Flex, Icon, Link, Text } from "@gravity-ui/uikit";
import cn from "classnames";
import Skeleton from "react-loading-skeleton";

import { formatDeliveryRange } from "../../../../../utils";
import { DeliveryRangeDto } from "../../../../../VariantTreeDto";
import { useProductInfoTranslate } from "./hooks";
import css from "./ProductInfoCard.module.scss";

interface ProductInfoCardProps {
  price?: number;
  currency?: string;
  delivery?: DeliveryRangeDto;
  deliveryBlur?: string;
  store?: string;
  supplierName?: string;
  url?: string;
  isLoading: boolean;
  isBlur: boolean;
  promptText?: string;
}

export const ProductInfoCard = ({
  price,
  currency,
  delivery,
  deliveryBlur,
  store,
  supplierName,
  url,
  isLoading,
  isBlur,
  promptText,
}: ProductInfoCardProps) => {
  const translations = useProductInfoTranslate();

  const deliveryText = isBlur
    ? deliveryBlur
    : formatDeliveryRange(delivery, {
        day: translations.delivery_days,
        hour: translations.delivery_hours,
      });

  return (
    <Card
      className={cn(css.mainInfoHeader, {
        [css.promptCard]: promptText,
      })}
      view="outlined"
      type="action"
      size="l"
    >
      <Flex direction="column" gap="1">
        {!isLoading ? (
          promptText ? (
            <Flex alignItems="center" gap="2" className={css.promptHint}>
              <Icon data={HandPointUp} size={20} />
              <Text>{promptText}</Text>
            </Flex>
          ) : (
            <>
              {store && (
                <Flex
                  gap="2"
                  alignItems="center"
                  justifyContent="space-between"
                  className={cn({ [css.blur]: isBlur })}
                >
                  <Flex direction="column" className={css.storeName}>
                    <Text variant="subheader-2">{store}</Text>
                    {supplierName && (
                      <Text variant="caption-2" color="secondary" ellipsis>
                        {supplierName}
                      </Text>
                    )}
                  </Flex>

                  {url && (
                    <Link
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      title={translations.open_store}
                    >
                      <Icon data={ArrowUpRightFromSquare} size={14} />
                    </Link>
                  )}
                </Flex>
              )}

              <Text
                variant="header-1"
                className={cn(css.price, { [css.blur]: isBlur })}
              >
                {price ? `${price} ${currency ?? ""}` : "—"}
              </Text>

              {deliveryText && (
                <Text
                  variant="body-2"
                  color="secondary"
                  className={cn(css.price, { [css.blur]: isBlur })}
                >
                  {deliveryText}
                </Text>
              )}
            </>
          )
        ) : (
          <>
            <Skeleton width="max" height={24} className={css.skeletonCard} />
            <Skeleton width="max" height={20} className={css.skeletonCard} />
          </>
        )}
      </Flex>
    </Card>
  );
};
